<template>
  <div v-if="selectedCourse" class="course-details">
    <section class="hero-section">
      <div class="overlay"></div>
      <div class="hero-content">
        <h1 class="title-font">{{ selectedCourse.title }}</h1>
        <p>{{ selectedCourse.details.description }}</p>
      </div>
    </section>

    <section class="course-included section-padding">
      <div class="container included">
        <h2 class="section-title fade-in" v-fade-in>What's Included</h2>
        <p class="included-note fade-in" v-fade-in>
          {{ selectedCourse.details.note }}
        </p>
        <ul class="included-list">
          <li
            class="fade-in"
            v-fade-in
            v-for="(module, index) in selectedCourse.details.modules"
            :key="index"
          >
            <i class="fas fa-check-circle"></i>
            <div>
              <strong>{{ module.title }}</strong>
              <p>{{ module.content }}</p>
            </div>
          </li>
        </ul>
      </div>
    </section>

    <section class="cta-section text-center">
      <h3 class="title-font">Ready to Start?</h3>
      <p>
        If this course is right for you, check out
        <router-link to="/plans" class="cta-link">Our Plans</router-link>
        to choose the best option that fits your schedule and budget.
      </p>
    </section>
  </div>
  <div v-else class="loading-message">
    <p>Loading course details...</p>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { useHead } from "@vueuse/head";

export default {
  name: "CourseDetailsView",
  computed: {
    ...mapGetters(["selectedCourse", "getCourses"]),
  },
  methods: {
    ...mapActions(["loadCourses", "selectCourse"]),
    async fetchPlans() {
      try {
        const response = await fetch("/data/plans.json");
        if (!response.ok) throw new Error("Error loading plans");
        return await response.json();
      } catch (error) {
        console.error("Plans fetch error:", error);
        return null;
      }
    },
  },
  async created() {
    const slug = this.$route.params.slug;

    if (!this.getCourses.length) {
      await this.loadCourses();
    }

    this.selectCourse(slug);

    // ✅ إضافة Schema.org بعد تحميل البيانات
    if (this.selectedCourse) {
      const siteUrl = "https://www.al-furqanacademy.com";
      const ogImage = `${siteUrl}/images/search_logo.webp`;

      const plansData = await this.fetchPlans();
      let lowPrice = 0;
      let highPrice = 0;

      if (plansData && plansData.plans) {
        const allPlans = [
          ...plansData.plans["30min_classes"],
          ...plansData.plans["1hour_classes"],
        ];
        lowPrice = Math.min(...allPlans.map((p) => p.price_per_hour_usd));
        highPrice = Math.max(...allPlans.map((p) => p.price_per_hour_usd));
      }

      useHead({
        title: `${this.selectedCourse.title} | Al-Furqan Academy`,
        meta: [
          {
            name: "description",
            content: this.selectedCourse.details.description,
          },
          { property: "og:title", content: this.selectedCourse.title },
          {
            property: "og:description",
            content: this.selectedCourse.details.description,
          },
          { property: "og:image", content: ogImage },
        ],
        script: [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Course",
              name: this.selectedCourse.title,
              description: this.selectedCourse.details.description,
              image: ogImage,
              provider: {
                "@type": "EducationalOrganization",
                name: "Al-Furqan Academy",
                url: siteUrl,
                logo: `${siteUrl}/images/logo.webp`,
                sameAs: [
                  "https://www.facebook.com/people/Al-Furqan-Academy/61571651244265/",
                  "https://www.tiktok.com/@furqanacademy101?_t=ZS-8xE1tXqCpPd&_r=1",
                  "https://www.youtube.com/@alfurqanacademy-k6t",
                ],
              },
              offers: {
                "@type": "AggregateOffer",
                priceCurrency: "USD",
                lowPrice: lowPrice.toString(),
                highPrice: highPrice.toString(),
                offerCount: "8",
                url: `${siteUrl}/plans`,
              },
              hasCourseInstance: {
                "@type": "CourseInstance",
                courseMode: "Online",
                location: {
                  "@type": "VirtualLocation",
                  url: siteUrl,
                },
              },
            }),
          },
        ],
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.course-details {
  /* ----------- HERO SECTION ----------- */
  .hero-section {
    position: relative;
    height: 450px;
    background: url("../../public/images/section_background.webp") no-repeat
      center center/cover;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 1em;
    text-align: center;

    .overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.45);
    }

    .hero-content {
      position: relative;
      color: #fff;
      z-index: 2;
      max-width: 1100px;
      width: 100%;
      padding: 0 1em;

      h1 {
        font-size: 3em;
        font-weight: 700;
        margin-bottom: 0.5em;

        @media (max-width: 992px) {
          font-size: 2.4em;
        }

        @media (max-width: 768px) {
          font-size: 2em;
        }
      }

      p {
        font-size: 1.3em;
        font-weight: 300;
        line-height: 1.8;
        max-width: 900px;
        margin: 0 auto;

        @media (max-width: 992px) {
          font-size: 1.1em;
        }

        @media (max-width: 768px) {
          font-size: 1em;
        }
      }
    }
  }

  /* ----------- WHAT'S INCLUDED SECTION ----------- */
  .course-included {
    background: var(--background);
    padding: 4em 1.5em;

    .included {
      max-width: 1200px;
      margin: 0 auto;
    }

    .section-title {
      color: var(--primary);
      font-size: 2.4em;
      text-align: center;
      margin-bottom: 0.3em;

      @media (max-width: 768px) {
        font-size: 2em;
      }
    }

    /* Note */
    .included-note {
      text-align: left; // Starts from the left
      color: var(--secondary-text);
      font-size: 1.3em;
      margin: 1em auto 2em auto;
      line-height: 1.8;
      max-width: 900px;

      @media (max-width: 768px) {
        text-align: center; // Centered on mobile
        font-size: 1em;
      }
    }

    .included-list {
      max-width: 1200px;
      margin: 0 auto;
      display: flex;
      flex-direction: column;
      gap: 2.5em;

      li {
        display: flex;
        align-items: flex-start;
        gap: 1.5em;
        font-size: 1.1em;
        text-align: left; // Starts from the left

        @media (max-width: 768px) {
          text-align: center; // Centered on mobile
          flex-direction: column; // Stacks elements vertically
          align-items: center;
        }

        i {
          color: var(--primary);
          font-size: 1.8em;
          flex-shrink: 0;
        }

        div {
          max-width: 1000px;
        }

        strong {
          display: block;
          font-size: 1.5em;
          color: var(--primary);
          margin-bottom: 0.4em;

          @media (max-width: 768px) {
            font-size: 1.3em;
          }
        }

        p {
          margin: 0;
          color: var(--secondary-text);
          font-size: 1.2em;
          line-height: 1.8;

          @media (max-width: 768px) {
            font-size: 1em;
          }
        }
      }
    }
  }

  /* ----------- CTA SECTION ----------- */
  .cta-section {
    background: var(--surface);
    padding: 3em 1em;
    text-align: center;

    h3 {
      color: var(--primary);
      margin-bottom: 1em;
      font-size: 1.8em;

      @media (max-width: 768px) {
        font-size: 1.5em;
      }
    }

    p {
      font-size: 1.2em;
      line-height: 1.6;

      @media (max-width: 768px) {
        font-size: 1em;
      }
    }

    .cta-link {
      color: var(--primary);
      font-weight: bold;
      text-decoration: underline;
      transition: color 0.3s ease;

      &:hover {
        color: darken(#3498db, 10%);
      }
    }
  }
}

/* ---------- Animation ---------- */
@keyframes fade-in {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
